import React, { useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { Message } from "primereact/message";

import { applyEroundMappings } from "../../../../services/onboarding/wallbox";
import { EroundMappingLine, WallboxEntry } from "../../../../types/onboarding";
import { MappingImportPreview, parseMappingProposal, previewMappingImport } from "../../../../utils/mappingProposal";
import { wallboxCatalogueCodeMessage } from "./wallboxCatalogueErrors";

// The import endpoint takes at most this many lines per request.
const CHUNK = 500;

interface Outcome {
  total: number;
  mapped: number;
  hidden: number;
  skipped: number;
  failed: string[];
  stoppedAt: number | null;
}

interface ImportMappingsDialogProps {
  visible: boolean;
  entries: WallboxEntry[];
  onHide: () => void;
  onApplied: () => void;
}

/** Applies a reviewed eRound mapping proposal (SOPS-169), previewing it before anything is sent. */
const ImportMappingsDialog: React.FC<ImportMappingsDialogProps> = ({ visible, entries, onHide, onApplied }) => {
  const upload = useRef<FileUpload>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [preview, setPreview] = useState<MappingImportPreview | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [applying, setApplying] = useState(false);

  // PrimeReact does not export its handler event type; only `files` is used.
  const onSelect = async (event: { files: File[] }) => {
    const file = event.files?.[0];
    upload.current?.clear();
    if (!file) return;
    setFileName(file.name);
    setOutcome(null);
    const parsed = parseMappingProposal(await file.text());
    setProblems(parsed.problems);
    setPreview(previewMappingImport(parsed.lines, entries));
  };

  const apply = async (lines: EroundMappingLine[]) => {
    setApplying(true);
    const result: Outcome = { total: lines.length, mapped: 0, hidden: 0, skipped: 0, failed: [], stoppedAt: null };
    for (let start = 0; start < lines.length; start += CHUNK) {
      try {
        const applied = await applyEroundMappings(lines.slice(start, start + CHUNK));
        result.mapped += applied.mapped;
        result.hidden += applied.hidden.length;
        result.skipped += applied.skipped.length;
        const failed = applied.failed.map((f) => `${f.manufacturer} – ${f.model}: ${wallboxCatalogueCodeMessage(f.error_code)}`);
        result.failed.push(...failed);
      } catch (err) {
        console.error("The eRound mapping import stopped", err);
        result.stoppedAt = start;
        break;
      }
    }
    setOutcome(result);
    setApplying(false);
    onApplied();
  };

  return (
    <Dialog header="Import eRound mappings" visible={visible} onHide={onHide} style={{ width: "48rem" }}>
      <p className="mt-0 text-color-secondary line-height-3">
        Choose a reviewed proposal from <code>catalogue-propose</code>. Lines marked <em>map</em> get their eRound
        mapping and are shown to customers, lines marked <em>hide</em> are hidden from customers, and blank lines
        are left as they are.
      </p>
      <div className="flex align-items-center gap-3 mb-3">
        <FileUpload
          ref={upload}
          mode="basic"
          name="proposal"
          accept=".csv"
          customUpload
          auto
          uploadHandler={onSelect}
          chooseLabel={fileName ? "Choose another file" : "Choose proposal file"}
          disabled={applying}
        />
        {fileName && <span className="text-sm text-color-secondary">{fileName}</span>}
      </div>

      {problems.length > 0 && (
        <Message
          severity="warn"
          className="w-full mb-3"
          content={
            <ul className="m-0 pl-3">
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          }
        />
      )}

      {preview && !outcome && (
        <>
          <p>{`${preview.counts.map} to map · ${preview.counts.hide} to hide · ${preview.counts.blank} left blank (not sent)`}</p>
          {preview.notInCatalogue.length > 0 && (
            <>
              <p className="mb-1">Not in the catalogue, so not sent:</p>
              <ul className="mt-0">
                {preview.notInCatalogue.map((ref, i) => (
                  <li key={i}>{`${ref.manufacturer} – ${ref.model}`}</li>
                ))}
              </ul>
            </>
          )}
          <Button
            label={`Apply ${preview.send.length} changes`}
            loading={applying}
            disabled={applying || preview.send.length === 0}
            onClick={() => apply(preview.send)}
          />
        </>
      )}

      {outcome && (
        <>
          {outcome.stoppedAt !== null && (
            <Message
              severity="error"
              className="w-full mb-3"
              text={`The import stopped after ${outcome.stoppedAt} of ${outcome.total} lines. Import the file again to finish.`}
            />
          )}
          <p>{`Mapped ${outcome.mapped} · hidden ${outcome.hidden} · skipped ${outcome.skipped} · failed ${outcome.failed.length}`}</p>
          {outcome.failed.length > 0 && (
            <ul className="mt-0">
              {outcome.failed.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </Dialog>
  );
};

export default ImportMappingsDialog;
